
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../lib/supabase';

export const useSales = () => {
  const queryClient = useQueryClient();

  // Get sales orders
  const salesOrders = useQuery({
    queryKey: ['salesOrders'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sales_orders')
        .select(`
          *,
          customers(name, phone, email),
          products(model_name, category, vendor_id),
          vendors(name)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    }
  });

  // Get current gold rate
  const goldRate = useQuery({
    queryKey: ['goldRate'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gold_rates')
        .select('*')
        .order('date', { ascending: false })
        .limit(1)
        .single();
      
      if (error) {
        // Return default rate if no data
        return { rate_24k: 6500, rate_22k: 5950, rate_18k: 4875, date: new Date().toISOString().split('T')[0] };
      }
      return data;
    }
  });

  // Create sales order
  const createOrder = useMutation({
    mutationFn: async (orderData: any) => {
      const orderNumber = `ORD${Date.now()}`;
      
      const { data, error } = await supabase
        .from('sales_orders')
        .insert({
          ...orderData,
          order_number: orderNumber,
          salesman_id: 'current-user-id', // Replace with actual user ID
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['salesOrders'] });
    }
  });

  // Update order status
  const updateOrderStatus = useMutation({
    mutationFn: async ({ orderId, status, notes }: { orderId: string; status: string; notes?: string }) => {
      const { data, error } = await supabase
        .from('sales_orders')
        .update({ 
          status,
          updated_at: new Date().toISOString(),
          ...(notes && { disputes: { notes, updated_at: new Date().toISOString() } })
        })
        .eq('id', orderId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['salesOrders'] });
    }
  });

  // Calculate price based on gold rate and product details
  const calculatePrice = (product: any, goldRate: number, quantity: number = 1) => {
    if (!product || !goldRate) return 0;

    // Base gold price calculation
    const purity = product.purity_options?.[0]?.purity || '22K';
    const purityMultiplier = purity === '24K' ? 1 : purity === '22K' ? 0.916 : 0.75;
    
    const goldValue = (goldRate * purityMultiplier * product.gross_weight) || 0;
    const makingCharges = product.making_charges || 0;
    const valueAddition = product.value_addition || 0;
    
    // Stone value calculation (simplified)
    let stoneValue = 0;
    if (product.stone_details && product.stone_details.length > 0) {
      stoneValue = product.stone_details.reduce((total: number, stone: any) => {
        const baseRate = stone.is_precious ? 5000 : 500; // Simplified rate
        return total + (baseRate * (stone.weight_range?.max || 1));
      }, 0);
    }

    const basePrice = goldValue + makingCharges + valueAddition + stoneValue;
    
    // Apply margin if set
    const margin = product.margin_percentage || 0;
    const finalPrice = basePrice * (1 + margin / 100);
    
    return finalPrice * quantity;
  };

  // Get user sessions
  const userSessions = useQuery({
    queryKey: ['userSessions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_sessions')
        .select(`
          *,
          customers(name, phone, email)
        `)
        .order('session_start', { ascending: false });
      
      if (error) throw error;
      return data;
    }
  });

  // Create user session
  const createSession = useMutation({
    mutationFn: async (sessionData: any) => {
      const { data, error } = await supabase
        .from('user_sessions')
        .insert({
          ...sessionData,
          salesman_id: 'current-user-id', // Replace with actual user ID
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userSessions'] });
    }
  });

  // New helper functions
  const getProductById = (productId: string) => {
    const products = mockProducts.filter(p => p.status === 'active');
    return products.find(p => p.id === productId);
  };

  const getCurrentGoldRate = () => {
    return 6850; // Current gold rate per gram (24K)
  };

  // Cart Management
  const [cartItems, setCartItems] = useState<any[]>([]);

  const addToCart = (product: any, quantity: number = 1) => {
    const existingItem = cartItems.find(item => item.product.id === product.id);
    
    if (existingItem) {
      updateCartItem(existingItem.id, existingItem.quantity + quantity);
    } else {
      const cartItem = {
        id: `cart_${Date.now()}_${product.id}`,
        product,
        quantity,
        price: product.calculated_price || 0,
        total: (product.calculated_price || 0) * quantity
      };
      setCartItems(prev => [...prev, cartItem]);
    }
  };

  const updateCartItem = (itemId: string, newQuantity: number) => {
    setCartItems(prev => prev.map(item => 
      item.id === itemId 
        ? { ...item, quantity: newQuantity, total: item.price * newQuantity }
        : item
    ));
  };

  const removeFromCart = (itemId: string) => {
    setCartItems(prev => prev.filter(item => item.id !== itemId));
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotals = () => {
    const subtotal = cartItems.reduce((sum, item) => sum + item.total, 0);
    const makingCharges = cartItems.reduce((sum, item) => sum + (item.product.making_charge || 0) * item.quantity, 0);
    const gstRate = 0.03; // 3% GST
    const gst = Math.round((subtotal + makingCharges) * gstRate);
    const total = subtotal + makingCharges + gst;
    const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

    return {
      subtotal,
      makingCharges,
      gst,
      total,
      totalItems
    };
  };

  // Sales Order Management
  const createSalesOrder = useMutation({
    mutationFn: async (orderData: any) => {
      // Simulate API call
      const order = {
        id: `ORD_${Date.now()}`,
        ...orderData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      // Store in localStorage for demo
      const existingOrders = JSON.parse(localStorage.getItem('sales_orders') || '[]');
      existingOrders.push(order);
      localStorage.setItem('sales_orders', JSON.stringify(existingOrders));
      
      return order;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
    }
  });

  // Customer Management
  const getCustomerById = (customerId: string) => {
    const customers = JSON.parse(localStorage.getItem('customers') || '[]');
    return customers.find((customer: any) => customer.id === customerId);
  };

  const searchCustomers = (searchTerm: string) => {
    const customers = JSON.parse(localStorage.getItem('customers') || '[]');
    return customers.filter((customer: any) => 
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone.includes(searchTerm) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const createCustomer = useMutation({
    mutationFn: async (customerData: any) => {
      const customer = {
        id: `CUST_${Date.now()}`,
        ...customerData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      const existingCustomers = JSON.parse(localStorage.getItem('customers') || '[]');
      existingCustomers.push(customer);
      localStorage.setItem('customers', JSON.stringify(existingCustomers));
      
      return customer;
    }
  });

  return {
    salesOrders,
    goldRate,
    userSessions,
    createOrder,
    updateOrderStatus,
    createSession,
    calculatePrice,
    getProductById,
    getCurrentGoldRate,
    
    // Cart functions
    cartItems,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    getCartTotals,
    
    // Order functions
    createSalesOrder,
    
    // Customer functions
    getCustomerById,
    searchCustomers,
    createCustomer
  };
};
